import React, { useState, useEffect, useMemo } from "react";
import { Search, X, Save, ChevronLeft, Mic2, Edit3 } from "lucide-react";
import { db } from "../firebase";
import {
  collection, onSnapshot, query, orderBy,
  doc, updateDoc, serverTimestamp, deleteDoc,
} from "firebase/firestore";

const cx = (...list) => list.filter(Boolean).join(" ");

function AInput({ value, onChange, placeholder, disabled }) {
  return (
    <input type="text" value={value} onChange={onChange} placeholder={placeholder} disabled={disabled}
      className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm text-slate-800
        focus:outline-none focus:ring-2 focus:ring-slate-900 focus:border-transparent
        disabled:bg-slate-50 disabled:text-slate-400 placeholder:text-slate-400 transition" />
  );
}

function ATextarea({ value, onChange, placeholder, rows = 3 }) {
  return (
    <textarea value={value} onChange={onChange} placeholder={placeholder} rows={rows}
      className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm text-slate-800
        focus:outline-none focus:ring-2 focus:ring-slate-900 focus:border-transparent
        placeholder:text-slate-400 transition resize-none" />
  );
}

function Field({ label, children }) {
  return (
    <div>
      {label && <label className="block text-sm font-medium text-slate-700 mb-1.5">{label}</label>}
      {children}
    </div>
  );
}

function SectionHeading({ children }) {
  return (
    <div className="flex items-center gap-2 pt-2 pb-1">
      <span className="text-xs font-semibold text-slate-400 uppercase tracking-widest">{children}</span>
      <div className="flex-1 h-px bg-slate-100" />
    </div>
  );
}

function interviewToForm(id, d) {
  return {
    _fbId: id,
    title: d.title || "",
    guest: d.guest || "",
    interviewer: d.interviewer || "",
    summary: d.summary || "",
    videoUrl: d.videoUrl || "",
  };
}

export default function InterviewEditTab({ loading, setLoading, showStatus }) {
  const [interviews, setInterviews] = useState([]);
  const [dataLoading, setDataLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [selected, setSelected] = useState(null);

  useEffect(() => {
    const q = query(collection(db, "roportajlar"), orderBy("createdAt", "desc"));
    const unsub = onSnapshot(q, (snap) => {
      setInterviews(snap.docs.map((d) => ({ id: d.id, ...d.data() })));
      setDataLoading(false);
    }, () => setDataLoading(false));
    return () => unsub();
  }, []);

  const filtered = useMemo(() => {
    const q = search.trim().toLocaleLowerCase("tr");
    if (!q) return interviews;
    return interviews.filter((item) =>
      `${item.title || ""} ${item.guest || ""} ${item.interviewer || ""}`
        .toLocaleLowerCase("tr").includes(q)
    );
  }, [interviews, search]);

  const set = (patch) => setSelected((p) => ({ ...p, ...patch }));

  const handleSelect = (item) => {
    setSelected(interviewToForm(item.id, item));
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const handleDelete = async (e, item) => {
    e.stopPropagation();
    if (!window.confirm(`"${item.title || "Bu röportaj"}" silinsin mi?`)) return;
    try {
      await deleteDoc(doc(db, "roportajlar", item.id));
      showStatus("success", "Röportaj silindi.");
    } catch (err) {
      showStatus("error", "Silinemedi: " + err.message);
    }
  };

  const handleSave = async (e) => {
    e.preventDefault();
    if (!selected) return;
    setLoading(true);
    try {
      const { _fbId, ...rest } = selected;
      await updateDoc(doc(db, "roportajlar", _fbId), {
        ...rest,
        updatedAt: serverTimestamp(),
      });
      showStatus("success", "Röportaj güncellendi.");
      setSelected(null);
    } catch (err) {
      showStatus("error", "Hata: " + err.message);
    } finally {
      setLoading(false);
    }
  };

  /* ── Düzenleme formu ── */
  if (selected) return (
    <div>
      <button type="button" onClick={() => setSelected(null)}
        className="flex items-center gap-1.5 text-sm text-slate-500 hover:text-slate-800 mb-5 transition">
        <ChevronLeft className="w-4 h-4" /> Listeye Dön
      </button>

      <div className="flex items-center gap-2 mb-4">
        <Edit3 className="w-4 h-4 text-amber-500" />
        <h3 className="font-semibold text-slate-900 text-sm">Röportajı Düzenle</h3>
      </div>

      <form onSubmit={handleSave} className="space-y-5">
        <SectionHeading>Temel Bilgiler</SectionHeading>
        <div className="space-y-3">
          <Field label="Röportaj Başlığı">
            <AInput value={selected.title} onChange={(e) => set({ title: e.target.value })} placeholder="Başlık…" />
          </Field>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <Field label="Konuk">
              <AInput value={selected.guest} onChange={(e) => set({ guest: e.target.value })} placeholder="Konuk adı…" />
            </Field>
            <Field label="Röportajı Yapan">
              <AInput value={selected.interviewer} onChange={(e) => set({ interviewer: e.target.value })} placeholder="Yapan kişi…" />
            </Field>
          </div>
        </div>

        <SectionHeading>İçerik</SectionHeading>
        <Field label="Kısa Özet">
          <ATextarea value={selected.summary} onChange={(e) => set({ summary: e.target.value })}
            placeholder="Röportaj özeti…" rows={5} />
        </Field>

        <SectionHeading>Video</SectionHeading>
        <Field label="Video URL (YouTube / Vimeo)">
          <AInput value={selected.videoUrl} onChange={(e) => set({ videoUrl: e.target.value })}
            placeholder="https://youtube.com/…" />
        </Field>

        {/* Video önizleme */}
        {selected.videoUrl && selected.videoUrl.includes("youtube") && (
          <div className="rounded-xl overflow-hidden border border-slate-200 aspect-video">
            <iframe
              src={selected.videoUrl.replace("watch?v=", "embed/")}
              className="w-full h-full"
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
              allowFullScreen
              title="video önizleme"
            />
          </div>
        )}

        <div className="pt-2 flex gap-3">
          <button type="submit" disabled={loading}
            className="inline-flex items-center gap-2 bg-slate-900 hover:bg-slate-700 text-white px-5 py-2.5 rounded-xl text-sm font-semibold transition disabled:opacity-50">
            <Save className="w-4 h-4" />
            {loading ? "Kaydediliyor…" : "Güncelle"}
          </button>
          <button type="button" onClick={() => setSelected(null)}
            className="px-4 py-2.5 rounded-xl text-sm border border-slate-200 text-slate-600 hover:bg-slate-50 transition">
            İptal
          </button>
        </div>
      </form>
    </div>
  );

  /* ── Liste görünümü ── */
  return (
    <div>
      <div className="flex items-center gap-2 mb-4">
        <Mic2 className="w-4 h-4 text-amber-500" />
        <h3 className="font-semibold text-slate-900 text-sm">Röportajlar</h3>
        <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-slate-100 text-slate-500 font-medium">
          {interviews.length}
        </span>
      </div>

      {/* Arama */}
      <div className="relative mb-3">
        <Search className="w-4 h-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none" />
        <input value={search} onChange={(e) => setSearch(e.target.value)}
          placeholder="Başlık, konuk veya röportajı yapan ara…"
          className="w-full pl-9 pr-9 py-2.5 text-sm border border-slate-200 rounded-xl
            focus:outline-none focus:ring-2 focus:ring-slate-900 bg-slate-50 placeholder:text-slate-400" />
        {search && (
          <button onClick={() => setSearch("")}
            className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600">
            <X className="w-3.5 h-3.5" />
          </button>
        )}
      </div>

      <p className="text-xs text-slate-400 mb-3">
        <span className="font-medium text-slate-600">{filtered.length}</span> röportaj listeleniyor
      </p>

      {dataLoading && (
        <div className="space-y-2">
          {Array.from({ length: 4 }).map((_, i) => (
            <div key={i} className="h-14 bg-slate-100 rounded-xl animate-pulse" />
          ))}
        </div>
      )}

      {!dataLoading && (
        <div className="space-y-2 max-h-[600px] overflow-y-auto pr-1">
          {filtered.map((item) => (
            <button key={item.id} type="button" onClick={() => handleSelect(item)}
              className="w-full flex items-center gap-3 p-3 bg-white rounded-xl border border-slate-200
                hover:border-slate-300 hover:shadow-sm transition text-left group">

              {/* İkon */}
              <div className="w-10 h-10 rounded-lg border border-slate-100 bg-amber-50 shrink-0 flex items-center justify-center">
                <Mic2 className="w-4 h-4 text-amber-400" />
              </div>

              {/* Bilgi */}
              <div className="flex-1 min-w-0">
                <span className="font-medium text-sm text-slate-900 truncate block">
                  {item.title || "—"}
                </span>
                <span className="text-xs text-slate-400 truncate block">
                  {[item.guest, item.interviewer].filter(Boolean).join(" · ") || "—"}
                </span>
              </div>

              {/* Sil */}
              <button type="button" onClick={(e) => handleDelete(e, item)}
                className="shrink-0 p-1.5 rounded-lg text-slate-300 hover:text-red-500 hover:bg-red-50 transition"
                title="Sil">
                <X className="w-4 h-4" />
              </button>

              <Edit3 className="w-3.5 h-3.5 text-slate-300 group-hover:text-slate-500 shrink-0 transition" />
            </button>
          ))}

          {filtered.length === 0 && (
            <div className="text-center py-12 text-slate-400">
              <Mic2 className="w-8 h-8 mx-auto mb-2 text-slate-200" />
              <p className="text-sm">Röportaj bulunamadı.</p>
            </div>
          )}
        </div>
      )}
    </div>
  );
}